<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <!-- Only show in DEV mode -->
  <div v-if="isDev">
    <!-- Reset Database Button -->
    <button
      @click="handleReset"
      :disabled="loading"
      class="w-full flex items-center gap-2 bg-red-100 dark:bg-red-900 p-4 rounded-lg shadow hover:bg-red-200 dark:hover:bg-red-800 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border-2 border-red-400 dark:border-red-600">
      <div class="flex-1 text-left">
        <div class="font-semibold text-red-900 dark:text-red-100">
          {{ loading ? 'Resetting Database...' : 'Reset Database (Drop All Tables)' }}
        </div>
        <div class="text-xs text-red-700 dark:text-red-300">
          {{ statusMessage || 'Delete all tables and data - requires app restart' }}
        </div>
      </div>
    </button>

    <!-- Confirmation Dialog -->
    <Teleport to="body">
      <div v-if="showConfirmDialog" class="module-overlay">
        <div class="module-backdrop" @click="showConfirmDialog = false"></div>
        
        <div class="module-container">
          <div class="module-content">
            <!-- Header -->
            <h3 class="text-xl font-bold text-center text-[var(--color-error)] mb-4">
              Confirm Database Reset
            </h3>
            
            <!-- Warning Message -->
            <p class="text-[var(--color-text)] mb-4">
              This will <strong>permanently delete ALL data</strong> including:
            </p>
            
            <!-- Data List -->
            <ul class="text-sm text-[var(--color-text)] opacity-70 mb-4 list-disc list-inside space-y-1">
              <li>All goats</li>
              <li>All groups</li>
              <li>All weight records</li>
              <li>All temperature records</li>
              <li>All FAMACHA records</li>
              <li>All DAG records</li>
            </ul>
            
            <!-- Restart Notice -->
            <p class="text-sm text-[var(--color-text)] opacity-80 mb-6 p-3 bg-[var(--color-primary-200)] rounded-lg border border-[var(--color-primary-400)]">
              You will need to <strong>close and reopen the app</strong> to recreate the database schema.
            </p>
            
            <!-- Action Buttons -->
            <div class="flex gap-3 justify-end">
              <button
                @click="showConfirmDialog = false"
                class="btn btn--secondary">
                Cancel
              </button>
              <button
                @click="confirmReset"
                class="px-4 py-2 bg-[var(--color-error)] text-white rounded-lg font-semibold hover:bg-[var(--color-error-dark)] transition-all border border-[var(--color-error)]">
                Yes, Delete Everything
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Success/Error Toast -->
    <Toast :show="showToast" :message="toastMessage" :type="toastType" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { databaseService } from '@/services/database/database.service.js'
import Toast from '@/components/ui/feedback/Toast.vue'

// Check if we're in development mode
const isDev = computed(() => import.meta.env.VITE_ENV === 'development')

// Component state
const loading = ref(false)
const statusMessage = ref('')
const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('saved')
const showConfirmDialog = ref(false)

/**
 * Handle reset button click - show confirmation first
 */
function handleReset() {
  showConfirmDialog.value = true
}

/**
 * Confirm and execute database reset
 */
async function confirmReset() {
  showConfirmDialog.value = false
  loading.value = true
  statusMessage.value = 'Dropping all tables...'

  try {
    const platform = databaseService.getPlatform()
    const adapter = databaseService.getAdapterName()
    
    console.log(`Resetting database on ${platform} using ${adapter}`)
    
    // Drop all tables
    await databaseService.dropAllTables()

    // Success!
    statusMessage.value = 'Database reset complete!'
    showSuccessToast(`Database reset on ${platform}. Please restart the app!`)

    // Reset status message after delay
    setTimeout(() => {
      statusMessage.value = ''
    }, 5000)
  } catch (error) {
    console.error('Failed to reset database:', error)
    statusMessage.value = 'Reset failed'
    showErrorToast(`Database reset failed: ${error.message}`)

    // Reset status message after delay
    setTimeout(() => {
      statusMessage.value = ''
    }, 3000)
  } finally {
    loading.value = false
  }
}

function showSuccessToast(message) {
  toastType.value = 'info'
  toastMessage.value = message
  showToast.value = true
}

function showErrorToast(message) {
  toastType.value = 'error'
  toastMessage.value = message
  showToast.value = true
}
</script>
